import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ArtisticObject,
  ArtisticObjectListing,
  PersonAssignment,
  UpdatePersonAssignment,
  UpdateWorkshopAssignment,
  WorkshopAssignment
} from '../types/ArtisticObject';
import {PlaceListing} from '../types/Place';
import {Section} from '../types/Section';
import artisticObjectsService from '../services/artisticObjectsService';
import placesService from '../services/placesService';
import dictionaryItemsService from '../../core/services/dictionaryItemsService';
import {handleError} from '../../../common/utils/errorHandling';
import {getParentOptions} from '../../../common/utils/dropdownUtils';
import {useNavigate, useParams} from 'react-router-dom';
import {DEFAULT_LANGUAGE} from '../../../common/i18n/config';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {
  AdminButton,
  AdminForm,
  AdminFormSection,
  AdminInputDropdown,
  AdminInputRow,
  AdminInputText,
  FieldLanguageSwitcher
} from "../../../layout/admin";
import {FileUpload} from "primereact/fileupload";
import imagesService from "../services/imagesService";
import AdminImagesSection from "../../../layout/admin/AdminImagesSection";
import {DictionaryItemListing, DictionaryType} from '../../core/types/DictionaryItem';
import ArtisanAssignments from "./ArtisanAssignments";
import CreationTime from "./CreationTime";
import {EMPTY_RANGES, Ranges} from "../types/Ranges";
import IconclassSelection from "./IconclassSelection";
import AdminTextSections from "../../../layout/admin/AdminTextSections";
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import {
  getTranslatedLanguages,
  getValueWithFallback,
  StringTranslations
} from '../../../common/types/StringTranslations';
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const ArtisticObjectsForm: React.FC = () => {
  const {t} = useTranslation();
  const nameField = useTranslatableField();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [formData, setFormData] = useState({
    placeId: '',
    creationTime: '',
    creationRanges: EMPTY_RANGES as Ranges,
    dimensions: '',
    locationCode: '',
    locationDescription: '',
    creators: '',
    designers: '',
    parentId: ''
  });
  const [personAssignments, setPersonAssignments] = useState<UpdatePersonAssignment[]>([]);
  const [workshopAssignments, setWorkshopAssignments] = useState<UpdateWorkshopAssignment[]>([]);
  const [selectedIconclasses, setSelectedIconclasses] = useState<string[]>([]);

  const {id} = useParams<{ id: string }>();
  const [artisticObject, setArtisticObject] = useState<ArtisticObject | undefined>();
  const [allPlaces, setAllPlaces] = useState<PlaceListing[]>([]);
  const [allArtisticObjects, setAllArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [sectionTypes, setSectionTypes] = useState<DictionaryItemListing[]>([]);
  const [sectionContent, setSectionContent] = useState<Map<string, StringTranslations>>(new Map());
  const fileUploadRef = useRef<FileUpload>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllPlaces();
    fetchAllArtisticObjects();
    fetchSectionTypes();
    if (id) {
      fetchArtisticObject(id);
    }
  }, [id]);

  const fetchAllPlaces = async (): Promise<void> => {
    await handleError(
      async () => {
        const placesData = await placesService.getAllPlaces();
        setAllPlaces(placesData);
      },
      t('admin.artisticObjects.errors.fetchingPlaces')
    );
  };

  const fetchAllArtisticObjects = async (): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await artisticObjectsService.getAllArtisticObjects();
        setAllArtisticObjects(objectsData);
      },
      t('admin.artisticObjects.errors.fetchingObjects')
    );
  };

  const fetchSectionTypes = async (): Promise<void> => {
    await handleError(
      async () => {
        const sectionTypesData = await dictionaryItemsService.getDictionaryItemsByType(DictionaryType.ARTISTIC_OBJECT_SECTION);
        setSectionTypes(sectionTypesData);
      },
      t('admin.artisticObjects.errors.fetchingSectionTypes')
    );
  };

  const fetchArtisticObject = async (objectId: string): Promise<void> => {
    await handleError(
      async () => {
        const objectData = await artisticObjectsService.getArtisticObject(objectId);
        setArtisticObject(objectData);
        nameField.setTranslations(objectData.name);
        setFormData({
          placeId: objectData.place.id,
          creationTime: objectData.creationTime || '',
          creationRanges: objectData.creationRanges || EMPTY_RANGES,
          dimensions: objectData.dimensions || '',
          locationCode: objectData.locationCode || '',
          locationDescription: objectData.locationDescription || '',
          creators: objectData.creators || '',
          designers: objectData.designers || '',
          parentId: objectData.parent?.id || '',
        });
        initializeSectionContent(objectData.sections || []);
        initializePersonAssignments(objectData.personAssignments || []);
        initializeWorkshopAssignments(objectData.workshopAssignments || []);
        setSelectedIconclasses(objectData.iconclasses?.map(ic => ic.code) || []);
      },
      t('admin.artisticObjects.errors.fetchingObject')
    );
  };

  const initializeSectionContent = (sections: Section[]): void => {
    const contentMap = new Map<string, StringTranslations>();

    // Group sections by type and build StringTranslations
    sections.forEach(section => {
      const typeAlias = section.type.alias;
      const existingTranslations = contentMap.get(typeAlias) || {};
      const updatedTranslations = {
        ...existingTranslations,
        [section.language]: section.content
      };
      contentMap.set(typeAlias, updatedTranslations);
    });

    setSectionContent(contentMap);
  };

  const initializePersonAssignments = (assignments: PersonAssignment[]): void => {
    const updateAssignments: UpdatePersonAssignment[] = assignments.map((assignment) => ({
      personId: assignment.id,
      type: assignment.type,
      uncertain: assignment.uncertain
    }));
    setPersonAssignments(updateAssignments);
  };

  const initializeWorkshopAssignments = (assignments: WorkshopAssignment[]): void => {
    const updateAssignments: UpdateWorkshopAssignment[] = assignments.map((assignment) => ({
      workshopId: assignment.id,
      type: assignment.type,
      uncertain: assignment.uncertain
    }));
    setWorkshopAssignments(updateAssignments);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreationTimeChange = (creationTime: string, ranges: Ranges) => {
    setFormData(prev => ({
      ...prev,
      creationTime,
      creationRanges: ranges
    }));
  };

  const handleSectionContentChange = (typeAlias: string, translations: StringTranslations): void => {
    setSectionContent(prev => new Map(prev).set(typeAlias, translations));
  };

  const buildSectionsForSubmission = (): Section[] => {
    const sections: Section[] = [];

    sectionContent.forEach((translations, typeAlias) => {
      const translatedLanguages = getTranslatedLanguages(translations);

      translatedLanguages.forEach(language => {
        const content = translations[language];
        if (content && content.trim()) {
          // Find existing section with same type and language
          const existingSection = artisticObject?.sections?.find(
            s => s.type.alias === typeAlias && s.language === language
          );

          sections.push({
            id: existingSection?.id || crypto.randomUUID(),
            type: sectionTypes.find(t => t.alias === typeAlias)!,
            content: content.trim(),
            language: language
          });
        }
      });
    });

    return sections;
  };

  const buildPersonAssignmentsForSubmission = (): UpdatePersonAssignment[] => {
    return personAssignments.filter(assignment => assignment.personId !== '');
  };

  const buildWorkshopAssignmentsForSubmission = (): UpdateWorkshopAssignment[] => {
    return workshopAssignments.filter(assignment => assignment.workshopId !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    try {
      const sections = buildSectionsForSubmission();
      const validPersonAssignments = buildPersonAssignmentsForSubmission();
      const validWorkshopAssignments = buildWorkshopAssignmentsForSubmission();

      if (artisticObject) {
        await artisticObjectsService.updateArtisticObject({
          ...artisticObject,
          name: nameField.translations,
          placeId: formData.placeId,
          creationTime: formData.creationTime || undefined,
          creationRanges: formData.creationRanges.ranges.length > 0 ? formData.creationRanges : undefined,
          dimensions: formData.dimensions || undefined,
          locationCode: formData.locationCode || undefined,
          locationDescription: formData.locationDescription || undefined,
          creators: formData.creators || undefined,
          designers: formData.designers || undefined,
          parentId: formData.parentId || undefined,
          sections,
          personAssignments: validPersonAssignments,
          workshopAssignments: validWorkshopAssignments,
          iconclasses: selectedIconclasses
        });
      } else {
        await artisticObjectsService.createArtisticObject({
          name: nameField.translations,
          placeId: formData.placeId,
          creationTime: formData.creationTime || undefined,
          creationRanges: formData.creationRanges.ranges.length > 0 ? formData.creationRanges : undefined,
          dimensions: formData.dimensions || undefined,
          locationCode: formData.locationCode || undefined,
          locationDescription: formData.locationDescription || undefined,
          creators: formData.creators || undefined,
          designers: formData.designers || undefined,
          parentId: formData.parentId || undefined,
          sections,
          personAssignments: validPersonAssignments,
          workshopAssignments: validWorkshopAssignments,
          iconclasses: selectedIconclasses
        });
      }
      navigate('/artistic-objects');
    } catch (error) {
      handleApiError(error);
    }
  };

  const placeOptions = allPlaces.map(place => ({
    label: place.name,
    value: place.id
  }));

  const currentArtisticObjectAsListing: ArtisticObjectListing | undefined = artisticObject ? {
    id: artisticObject.id,
    name: getValueWithFallback(artisticObject.name, DEFAULT_LANGUAGE),
    place: artisticObject.place,
    creationTime: artisticObject.creationTime,
    creationRanges: artisticObject.creationRanges,
    dimensions: artisticObject.dimensions,
    locationCode: artisticObject.locationCode,
    locationDescription: artisticObject.locationDescription,
    parent: artisticObject.parent,
    firstImage: artisticObject.images?.[0],
    iconclasses: artisticObject.iconclasses?.map(ic => ic.code)
  } : undefined;

  const parentOptions = getParentOptions(
    allArtisticObjects,
    currentArtisticObjectAsListing,
    (obj) => `${obj.name} ${obj.locationCode ? `(${obj.locationCode})` : ''}`
  );

  const handlePreviewClick = () => {
    if (id) {
      window.open(`/gallery/artistic-object/${id}`, '_blank');
    }
  };

  return (
    <>
      <AdminFormHeader
        title={artisticObject ? t('admin.artisticObjects.title.edit') : t('admin.artisticObjects.title.create')}
        subtitle={artisticObject ? t('admin.artisticObjects.subtitle.edit') : t('admin.artisticObjects.subtitle.create')}
        onPreviewClick={id ? handlePreviewClick : undefined}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection
              title={t('admin.common.sections.basicInfo')}
            >
              <AdminInputText
                label={t('admin.common.fields.name')}
                value={nameField.currentValue}
                onChange={(e) => nameField.setCurrentValue(e.target.value)}
                name="name"
                id="name"
                required={true}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={nameField.currentLanguage}
                    onLanguageChange={nameField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={nameField.translatedLanguages}
                  />
                }
                error={errors.name}
              />
              <AdminInputDropdown
                label={t('admin.artisticObjects.fields.place')}
                value={formData.placeId}
                onChange={handleDropdownChange}
                options={placeOptions}
                placeholder={t('admin.artisticObjects.placeholders.selectPlace')}
                name="placeId"
                id="placeId"
                required={true}
                error={errors.placeId}
              />
              <CreationTime
                creationTime={formData.creationTime}
                creationRanges={formData.creationRanges}
                onChange={handleCreationTimeChange}
              />
              <AdminInputText
                label={t('admin.artisticObjects.fields.dimensions')}
                value={formData.dimensions}
                onChange={handleInputChange}
                name="dimensions"
                id="dimensions"
                error={errors.dimensions}
              />
              <AdminInputRow>
                <AdminInputText
                  label={t('admin.artisticObjects.fields.locationCode')}
                  value={formData.locationCode}
                  onChange={handleInputChange}
                  name="locationCode"
                  id="locationCode"
                  error={errors.locationCode}
                />
                <AdminInputText
                  label={t('admin.artisticObjects.fields.locationDescription')}
                  value={formData.locationDescription}
                  onChange={handleInputChange}
                  name="locationDescription"
                  id="locationDescription"
                  error={errors.locationDescription}
                />
              </AdminInputRow>
            </AdminFormSection>

            <AdminTextSections
              title={t('admin.artisticObjects.sections.description')}
              sectionsContent={sectionContent}
              sectionTypes={
                ['HSTR', 'PPLK', 'FTNT'].map(alias => sectionTypes.find(t => t.alias === alias)).filter(Boolean) as DictionaryItemListing[]
              }
              handleSectionContentChange={handleSectionContentChange}
            />

            <AdminTextSections
              title={t('admin.artisticObjects.sections.other')}
              sectionsContent={sectionContent}
              sectionTypes={
                ['PRSR', 'NTES'].map(alias => sectionTypes.find(t => t.alias === alias)).filter(Boolean) as DictionaryItemListing[]
              }
              handleSectionContentChange={handleSectionContentChange}
            />

          </>
        }
        rightColumn={
          <>
            <AdminFormSection
              title={t('admin.artisticObjects.sections.relationships')}
            >
              <AdminInputDropdown
                label={t('admin.artisticObjects.fields.parentObject')}
                value={formData.parentId}
                onChange={handleDropdownChange}
                options={parentOptions}
                placeholder={t('admin.artisticObjects.placeholders.selectParent')}
                name="parentId"
                id="parentId"
                error={errors.parentId}
              />

            </AdminFormSection>

            <AdminFormSection title={t('admin.artisticObjects.sections.artisans')}>
              <ArtisanAssignments
                personAssignments={personAssignments}
                workshopAssignments={workshopAssignments}
                onPersonAssignmentsChange={setPersonAssignments}
                onWorkshopAssignmentsChange={setWorkshopAssignments}
                creatorsText={formData.creators}
                designersText={formData.designers}
                onCreatorsChange={(value) => setFormData(prev => ({...prev, creators: value}))}
                onDesignersChange={(value) => setFormData(prev => ({...prev, designers: value}))}
              />
            </AdminFormSection>

            {artisticObject && (
              <AdminImagesSection
                images={artisticObject?.images || []}
                fileUploadRef={fileUploadRef}
                objectId={artisticObject.id}
                fetchObject={(artisticObjectId) => fetchArtisticObject(artisticObjectId)}
                uploadImage={imagesService.uploadImageForArtisticObject}
                deleteImage={imagesService.deleteArtisticObjectImage}
                reorderImages={imagesService.reorderArtisticObjectImages}
              />
            )}

            <AdminTextSections
              title={t('admin.artisticObjects.sections.iconography')}
              sectionsContent={sectionContent}
              sectionTypes={
                ['ANSK', 'SNTR'].map(alias => sectionTypes.find(t => t.alias === alias)).filter(Boolean) as DictionaryItemListing[]
              }
              handleSectionContentChange={handleSectionContentChange}
            />

            <AdminFormSection title={t('admin.artisticObjects.sections.iconclasses')}>
              <IconclassSelection
                selectedIconclasses={selectedIconclasses}
                onIconclassesChange={setSelectedIconclasses}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/artistic-objects")}
            />
            <AdminButton
              type="submit"
              label={artisticObject ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
            />
          </>
        }
        onSubmit={handleSubmit}
      />

    </>
  );
};

export default ArtisticObjectsForm;
